import { Flame, Link2, Monitor, Smartphone } from 'lucide-react';
import React from 'react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  MockCard,
  ProductMockupBody,
  ProductMockupFrame,
  SkeletonBar
} from './shared';

const ToolbarRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  .slug {
    font-size: 13px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const DeviceToggle = styled('div')`
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 100px;
  padding: 3px;
  flex-shrink: 0;
`;

const DeviceSegment = styled('span')<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  background: ${(p) => (p.active ? '#0e1c2b' : 'transparent')};
  color: ${(p) => (p.active ? '#fff' : '#64748b')};

  svg {
    flex-shrink: 0;
  }
`;

const ArticleCard = styled(MockCard)`
  position: relative;
  overflow: hidden;
  padding: 18px 20px;
  min-height: 300px;
  min-width: 0;
`;

const ArticleHeading = styled('h3')`
  font-size: 13px;
  font-weight: 700;
  color: #0e1c2b;
  line-height: 1.4;
  max-width: 260px;
  margin: 0 0 12px;
`;

const SkeletonStack = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 7px;

  span {
    display: block;
  }
`;

const ProductRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin: 14px 0;
  min-width: 0;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const ProductColumn = styled('div')`
  min-width: 0;
`;

const ProductImage = styled('div')<{ gradient: string }>`
  height: 72px;
  border-radius: 8px;
  background: ${(p) => p.gradient};
  margin-bottom: 8px;
`;

const CtaPlaceholder = styled('div')`
  height: 20px;
  border-radius: 6px;
  background: #e7ebef;
  position: relative;
  margin-top: 8px;
`;

const HeatBubble = styled('div')<{
  size: number;
  bg: string;
  glow: string;
}>`
  position: absolute;
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  border-radius: 50%;
  background: ${(p) => p.bg};
  box-shadow: 0 0 0 8px ${(p) => p.glow};
  pointer-events: none;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const TooltipCard = styled('div')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  position: absolute;
  z-index: 2;
  background: #0e1c2b;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 12px 30px rgba(14, 28, 43, 0.35);
  max-width: calc(100% - 20px);
  min-width: 0;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const HotTooltip = styled(TooltipCard)`
  top: 28px;
  right: 10px;
  width: 190px;
`;

const ColdTooltip = styled(TooltipCard)`
  bottom: 14px;
  left: 10px;
  width: 160px;
`;

const TooltipHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;

  svg {
    flex-shrink: 0;
    color: #fff;
  }
`;

const MetricRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const MetricRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 10px;

  .label {
    color: #94a3b8;
    flex-shrink: 0;
  }

  .value {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #fff;
    font-weight: 600;
    text-align: right;
    min-width: 0;
  }
`;

const PlatformDot = styled('span')<{ color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex-shrink: 0;
`;

export const HeatmapMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <ProductMockupFrame ref={ref} aria-hidden>
      <AppMockupChrome product="Optimize" surface="Heatmaps" />
      <ProductMockupBody>
        <ToolbarRow>
          <span className="slug">/best-long-haul-luggage</span>
          <DeviceToggle>
            <DeviceSegment active>
              <Monitor size={11} />
              Desktop
            </DeviceSegment>
            <DeviceSegment>
              <Smartphone size={11} />
              Mobile
            </DeviceSegment>
          </DeviceToggle>
        </ToolbarRow>

        <ArticleCard>
          <ArticleHeading>
            How does this long-haul luggage stack up to competitors?
          </ArticleHeading>

          <SkeletonStack>
            <SkeletonBar w="100%" h={6} />
            <SkeletonBar w="92%" h={6} />
            <SkeletonBar w="78%" h={6} />
          </SkeletonStack>

          <ProductRow>
            <ProductColumn>
              <ProductImage
                gradient={`linear-gradient(135deg, ${COLORS.GOLD.gold2} 0%, ${COLORS.GOLD.gold3} 55%, ${COLORS.GOLD.gold4} 100%)`}
              />
              <SkeletonStack>
                <SkeletonBar w="90%" h={6} />
                <SkeletonBar w="70%" h={6} />
              </SkeletonStack>
              <CtaPlaceholder>
                <HeatBubble
                  size={34}
                  bg="rgba(46, 92, 255, 0.45)"
                  glow="rgba(46, 92, 255, 0.15)"
                />
              </CtaPlaceholder>
            </ProductColumn>
            <ProductColumn>
              <ProductImage
                gradient={`linear-gradient(135deg, ${COLORS.GOLD.gold6} 0%, ${COLORS.GOLD.gold7} 55%, ${COLORS.GOLD.gold8} 100%)`}
              />
              <SkeletonStack>
                <SkeletonBar w="90%" h={6} />
                <SkeletonBar w="70%" h={6} />
              </SkeletonStack>
              <CtaPlaceholder>
                <HeatBubble
                  size={64}
                  bg="rgba(245, 34, 45, 0.35)"
                  glow="rgba(245, 34, 45, 0.15)"
                />
              </CtaPlaceholder>
            </ProductColumn>
          </ProductRow>

          <SkeletonStack>
            <SkeletonBar w="100%" h={6} />
            <SkeletonBar w="96%" h={6} />
            <SkeletonBar w="60%" h={6} />
          </SkeletonStack>

          <HotTooltip visible={inView} delayMs={250} reduced={reduced}>
            <TooltipHeader>
              <Link2 size={12} />
              Buy at Away Travel
            </TooltipHeader>
            <MetricRows>
              <MetricRow>
                <span className="label">Platform</span>
                <span className="value">
                  <PlatformDot color="#2e5cff" />
                  Ascend
                </span>
              </MetricRow>
              <MetricRow>
                <span className="label">Revenue</span>
                <span className="value">$213.45</span>
              </MetricRow>
              <MetricRow>
                <span className="label">Clicks / CTR</span>
                <span className="value">100 / 3.3%</span>
              </MetricRow>
              <MetricRow>
                <span className="label">Conversions</span>
                <span className="value">6</span>
              </MetricRow>
              <MetricRow>
                <span className="label">EPC</span>
                <span className="value">
                  <Flame size={10} color="#ff4d4f" />
                  <span style={{ color: '#ff7875' }}>$2.13</span>
                </span>
              </MetricRow>
            </MetricRows>
          </HotTooltip>

          <ColdTooltip visible={inView} delayMs={450} reduced={reduced}>
            <TooltipHeader>
              <Link2 size={12} />
              Buy at Nordstrom
            </TooltipHeader>
            <MetricRows>
              <MetricRow>
                <span className="label">Platform</span>
                <span className="value">
                  <PlatformDot color="#7fa3ff" />
                  Skimlinks
                </span>
              </MetricRow>
              <MetricRow>
                <span className="label">EPC</span>
                <span className="value" style={{ color: '#7fa3ff' }}>
                  $0.13
                </span>
              </MetricRow>
            </MetricRows>
          </ColdTooltip>
        </ArticleCard>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
